import React from 'react';
import { Avatar, Button, Input, Modal, Popover, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import CommonTable from 'components/CommonTable';
import EligibilityStatusTag from 'components/EligibilityStatusTag';
import {
    EEligibilityStatus,
    ELIGIBILITY_PRICE_CENTS,
    ELIGIBILITY_TAG_BY_BACKEND_STATUS,
    headlineToken,
    TEligibilityInvoiceDetailVo,
    TEligibilityInvoiceLine,
    TEligibilityInvoiceListItem,
    TEligibilityInvoiceSortType,
    TEligibilityInvoiceStatus,
    TEligibilityInvoiceSummaryVo,
} from 'types/insuranceEligibility';
import {
    getEligibilityInvoiceDetail,
    getEligibilityInvoicePdf,
    getEligibilityInvoiceSummary,
    getEligibilityInvoices,
    getPracticeEligibilityInvoiceSummary,
    getPracticeEligibilityInvoices,
    getUnbilledEligibilityInvoice,
} from 'api/eligibility';
import { formatDateMDY, formatMoney } from 'pages/dashboard/InsuranceBillingPage/Dashboard/statusConfig';
import dayjs from 'utils/dayjs';
import s from './s.module.less';

const { Option } = Select;

// PDF 未生成时接口回 4xx；与 InsuranceBillingFees 的 NO_PDF 文案同一口径
const NO_PDF_MSG =
    'PDF not available yet. Open the invoice to see the full breakdown, or contact support if you need the file.';

const formatPeriod = (from?: string, to?: string): string => `${formatDateMDY(from)} - ${formatDateMDY(to)}`;

type TBadge = { label: string; cls: string; tip: string };

// 后端下发的是 provider_appointment_billing.billing_status（自动扣卡的结果），不是 invoice 表的
// INITIAL/SENT/PAID —— 别照搬兄弟 tab InsuranceBillingFees 的那套映射，两张表零交集。
// 也没有 Overdue：这笔账到点自动扣卡，不存在「到期未付」，扣不动就是 FAILED。
const PENDING_BADGE: TBadge = {
    label: 'Pending',
    cls: s.badgeAmber,
    tip: 'Invoice issued for this month. We charge the card on file on the due date.',
};

const BADGE_BY_STATUS: Record<TEligibilityInvoiceStatus, TBadge> = {
    PENDING: PENDING_BADGE,
    // 扣款在途（通常几分钟），不值得单开一个徽标，但 tip 要说清楚钱已经在走了
    PROCESSING: {
        label: 'Pending',
        cls: s.badgeAmber,
        tip: 'Your payment is going through now. This usually settles within a few minutes.',
    },
    SUCCEEDED: {
        label: 'Settled',
        cls: s.badgeGreen,
        tip: 'We charged the card on file for this invoice. Nothing more is owed for this month.',
    },
    FAILED: {
        label: 'Payment failed',
        cls: s.badgeRed,
        tip: "We couldn't charge the card on file for this invoice. Update your payment method to settle it.",
    },
    // 压根没发起扣款（没绑卡 / 收款账户没 ready）——要医生自己动手，不能混进 Pending
    PROVIDER_NOT_READY: {
        label: 'Not started',
        cls: s.badgeGray,
        tip: "No payment method on file, so this invoice hasn't been charged yet. Add a card to settle it.",
    },
    CANCELED: {
        label: 'Canceled',
        cls: s.badgeGray,
        tip: 'This invoice was canceled. Nothing is owed.',
    },
    INVOICE_PENDING: {
        label: 'Invoice pending',
        cls: s.badgeGray,
        tip: 'Charges accruing this month. Your invoice is generated at the start of next month.',
    },
};

// 后端新增枚举值时宁可落回 Pending，也不要渲染出空徽标
const badgeOf = (status: TEligibilityInvoiceStatus): TBadge => BADGE_BY_STATUS[status] || PENDING_BADGE;

const StatusBadge: React.FC<{ status: TEligibilityInvoiceStatus }> = ({ status }) => {
    const b = badgeOf(status);
    return (
        <div className={`${s.badge} ${b.cls}`}>
            {b.label}
            <Popover content={<div className={s.pop}>{b.tip}</div>} title={null} placement="right" trigger="hover">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M5.48511 6C5.85118 5.22321 6.83895 4.66667 8.00004 4.66667C9.4728 4.66667 10.6667 5.5621 10.6667 6.66667C10.6667 7.59963 9.81496 8.38338 8.66285 8.6044C8.30125 8.67377 8.00004 8.96514 8.00004 9.33333M8 11.3333H8.00667M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Popover>
        </div>
    );
};

const MetricCard: React.FC<{ label: string; tip: string; value: string }> = ({ label, tip, value }) => (
    <div className={s.metricCard}>
        <div className={s.metricLabel}>
            <span>{label}</span>
            <Popover trigger="hover" content={<div className={s.pop}>{tip}</div>}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Popover>
        </div>
        <div className={s.metricValue}>{value}</div>
    </div>
);

// UI 徽标筛选 → 后端 billing_status 枚举。这张表必须和 BADGE_BY_STATUS 是同一套词汇：
// 选项少一个，就等于表格里能看到某个徽标却没法按它筛；选项的值对不上，就是「列表显示
// Settled，筛 Settled 却筛不出来」。两处改动请一起改。
// 按账单生命周期排序，不按字母序——医生是顺着「这个月还在攒 → 出账 → 扣款结果」读的。
const STATUS_FILTERS = [
    { text: 'Invoice pending', value: 'INVOICE_PENDING' },
    { text: 'Pending', value: 'PENDING' },
    { text: 'Settled', value: 'SUCCEEDED' },
    { text: 'Payment failed', value: 'FAILED' },
    { text: 'Not started', value: 'PROVIDER_NOT_READY' },
    { text: 'Canceled', value: 'CANCELED' },
];
const STATUS_PARAM_BY_FILTER: Record<string, TEligibilityInvoiceStatus[]> = {
    // 当月未出账的虚拟行没有对应的 billing 记录，后端按约定恒置顶、不受该参数影响，
    // 所以选它等价于「只留那一行」
    INVOICE_PENDING: ['INVOICE_PENDING'],
    // 这两个值都渲染成 Pending 徽标，筛选也要一起带上
    PENDING: ['PENDING', 'PROCESSING'],
    SUCCEEDED: ['SUCCEEDED'],
    FAILED: ['FAILED'],
    PROVIDER_NOT_READY: ['PROVIDER_NOT_READY'],
    CANCELED: ['CANCELED'],
};

// 表头列 → 后端 sortType
const SORT_TYPE_BY_COLUMN: Record<string, TEligibilityInvoiceSortType> = {
    billingPeriod: 'BILLING_PERIOD',
    checksRun: 'CHECKS_RUN',
    billable: 'BILLABLE',
    fee: 'FEE',
    issueDate: 'ISSUED',
    dueDate: 'DUE',
};

// 发票行的 checkStatus（EligibilityStatusEnum，可能逗号多值）→ 前端词汇表 tag。
// 头条 token 与列表/详情同一套挑法（headlineToken，不是 tokens[0]）：同一次 check 三处不能各说各的
const lineTag = (checkStatus?: string): EEligibilityStatus => {
    const primary = headlineToken(
        (checkStatus || '')
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
    );
    return (primary && ELIGIBILITY_TAG_BY_BACKEND_STATUS[primary]) || EEligibilityStatus.CHECKED;
};

// 本 tab 仅非 program（manual）persona 消费：enrolled 的 check 和 advanced search 都免费
// （2026-09-16 拍板），没有 eligibility 发票，Financials 里不再渲染本组件
type Props = {
    // practice 聚合视图（§1.6/1.7）：跟随 Financials 一级 Practice View（group owner/admin 才为 true，
    // 由 InsuranceBillingTab 判定传入）；false/不传 = 个人视图。member 在页面层已整体隐藏
    practiceView?: boolean;
};

const EligibilityCheckInvoices: React.FC<Props> = ({ practiceView }) => {
    // summary 的时间窗（timeRangeMonths）；列表接口没有时间参数，表格恒为全部发票（服务端分页）
    const [months, setMonths] = React.useState(1);
    const [keyword, setKeyword] = React.useState('');
    const [providerKeyword, setProviderKeyword] = React.useState('');
    const [statusSel, setStatusSel] = React.useState<string[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [sorter, setSorter] = React.useState<{ sortType: TEligibilityInvoiceSortType; orderType: 'asc' | 'desc' }>({
        sortType: 'ISSUED',
        orderType: 'desc',
    });
    const isPractice = !!practiceView;

    const [summary, setSummary] = React.useState<TEligibilityInvoiceSummaryVo>();
    const [listLoading, setListLoading] = React.useState(false);
    const [records, setRecords] = React.useState<TEligibilityInvoiceListItem[]>([]);
    const [total, setTotal] = React.useState(0);

    const [detail, setDetail] = React.useState<TEligibilityInvoiceDetailVo | null>(null);
    // practice 视图详情弹窗的归属 provider 名（详情 VO 不带，点行时从行上取）
    const [detailProvider, setDetailProvider] = React.useState<string>();
    const [detailLoading, setDetailLoading] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            const vo = isPractice
                ? await getPracticeEligibilityInvoiceSummary(months)
                : await getEligibilityInvoiceSummary(months);
            if (!cancelled && vo) setSummary(vo);
        })();
        return () => {
            cancelled = true;
        };
    }, [months, isPractice]);

    React.useEffect(() => {
        let cancelled = false;
        setListLoading(true);
        (async () => {
            const req = {
                current: page,
                size: pageSize,
                sortType: sorter.sortType,
                orderType: sorter.orderType,
                invoiceNumber: keyword || undefined,
                status: statusSel.length ? statusSel.flatMap((v) => STATUS_PARAM_BY_FILTER[v] || []) : undefined,
                // 与 summary 同语义；虚拟 INVOICE_PENDING 行不受该参数影响、恒置顶
                timeRangeMonths: months,
            };
            const pageVo = isPractice
                ? await getPracticeEligibilityInvoices({ ...req, providerName: providerKeyword || undefined })
                : await getEligibilityInvoices(req);
            if (cancelled) return;
            setListLoading(false);
            setRecords(pageVo?.records || []);
            setTotal(pageVo?.total || 0);
        })();
        return () => {
            cancelled = true;
        };
    }, [page, pageSize, sorter, keyword, providerKeyword, statusSel, months, isPractice]);

    // invoiceId=null 是当月未出账的虚拟 INVOICE_PENDING 行 → 走 /unbilled 现算详情。
    // practice 视图看其他 provider 的 pending 行按行 providerId 带 override（self 请求层自动不发头）
    const openDetail = async (inv: Pick<TEligibilityInvoiceListItem, 'invoiceId' | 'providerId' | 'providerName'>) => {
        setDetailProvider(isPractice ? inv.providerName || undefined : undefined);
        setDetailLoading(true);
        try {
            const vo =
                inv.invoiceId != null
                    ? await getEligibilityInvoiceDetail(inv.invoiceId)
                    : await getUnbilledEligibilityInvoice(isPractice ? (inv.providerId ?? undefined) : undefined);
            if (vo) setDetail(vo);
        } finally {
            setDetailLoading(false);
        }
    };

    const downloadPdf = async (invoiceId: number) => {
        const url = await getEligibilityInvoicePdf(invoiceId);
        if (url) window.open(url, '_blank');
        else message.error(NO_PDF_MSG);
    };

    // practice 视图首列：归属 provider（头像+名+credential；不排序——§1.7 sortType 无 PROVIDER，已拍板不做）。
    // 存量合并行可能缺 provider 字段，兜底 "—"
    const providerColumn: ColumnsType<TEligibilityInvoiceListItem>[number] = {
        title: 'PROVIDER',
        key: 'provider',
        filteredValue: providerKeyword ? [providerKeyword] : null,
        filterIcon: (filtered: boolean) => <SearchOutlined style={filtered ? { color: '#0E62F4' } : undefined} />,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters: clear }) => (
            <div className={s.searchDropdown}>
                <Input
                    placeholder="Search provider"
                    value={(selectedKeys[0] as string) || ''}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    allowClear
                />
                <div className={s.searchDropdownBtns}>
                    <Button
                        size="small"
                        onClick={() => {
                            clear?.();
                            confirm();
                        }}
                    >
                        Reset
                    </Button>
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        Search
                    </Button>
                </div>
            </div>
        ),
        render: (_, inv) =>
            inv.providerName ? (
                <span className={s.providerCell}>
                    <Avatar size={40} src={inv.providerAvatar || undefined} icon={<UserOutlined />} />
                    <span className={s.providerMeta}>
                        <span className={s.providerName}>{inv.providerName}</span>
                        {inv.providerCredential ? (
                            <span className={s.providerCred}>{inv.providerCredential}</span>
                        ) : null}
                    </span>
                </span>
            ) : (
                '—'
            ),
    };

    const columns: ColumnsType<TEligibilityInvoiceListItem> = [
        {
            title: 'INVOICE #',
            key: 'invoiceNumber',
            dataIndex: 'invoiceNumber',
            filteredValue: keyword ? [keyword] : null,
            filterIcon: (filtered: boolean) => <SearchOutlined style={filtered ? { color: '#0E62F4' } : undefined} />,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters: clear }) => (
                <div className={s.searchDropdown}>
                    <Input
                        placeholder="Search invoice #"
                        value={(selectedKeys[0] as string) || ''}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        allowClear
                    />
                    <div className={s.searchDropdownBtns}>
                        <Button
                            size="small"
                            onClick={() => {
                                clear?.();
                                confirm();
                            }}
                        >
                            Reset
                        </Button>
                        <Button type="primary" size="small" onClick={() => confirm()}>
                            Search
                        </Button>
                    </div>
                </div>
            ),
        },
        ...(isPractice ? [providerColumn] : []),
        {
            title: 'BILLING PERIOD',
            key: 'billingPeriod',
            className: s.mut,
            sorter: true,
            render: (_, inv) => formatPeriod(inv.billingFrom, inv.billingTo),
        },
        {
            // 区间里跑过的全部次数（含缓存命中），和 BILLABLE 一起说明口径
            title: 'CHECKS RUN',
            key: 'checksRun',
            align: 'right',
            className: s.mut,
            sorter: true,
            render: (_, inv) => inv.checksRun,
        },
        {
            // CHECKS RUN 的计费子集
            title: 'BILLABLE',
            key: 'billable',
            align: 'right',
            className: s.mut,
            sorter: true,
            render: (_, inv) => inv.billable,
        },
        {
            // practice 视图里行是各 provider 的，"YOUR" 指代错位 → 通用 FEE
            title: isPractice ? 'FEE' : 'YOUR FEE',
            key: 'fee',
            align: 'right',
            sorter: true,
            render: (_, inv) => formatMoney(inv.fee),
        },
        {
            title: 'STATUS',
            key: 'status',
            filters: STATUS_FILTERS,
            filteredValue: statusSel.length ? statusSel : null,
            render: (_, inv) => <StatusBadge status={inv.status} />,
        },
        {
            title: 'ISSUED',
            key: 'issueDate',
            dataIndex: 'issueDate',
            className: s.mut,
            sorter: true,
            defaultSortOrder: 'descend',
            render: (v?: string) => formatDateMDY(v),
        },
        {
            title: 'DUE',
            key: 'dueDate',
            dataIndex: 'dueDate',
            className: s.mut,
            sorter: true,
            render: (v?: string) => formatDateMDY(v),
        },
        {
            title: 'ACTION',
            key: 'action',
            fixed: 'right',
            align: 'center',
            width: 170,
            render: (_, inv) => (
                <span className={s.actions}>
                    <span
                        className={s.link}
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetail(inv);
                        }}
                    >
                        View
                    </span>
                    {/* INVOICE_PENDING 行没有 Stripe invoice，不渲染 PDF 入口 */}
                    {inv.invoiceId != null && (
                        <span
                            className={s.link}
                            onClick={(e) => {
                                e.stopPropagation();
                                downloadPdf(inv.invoiceId!);
                            }}
                        >
                            <DownloadOutlined /> Download
                        </span>
                    )}
                </span>
            ),
        },
    ];

    // TODO(eligibility 二轮): 后端 Line 补 ranBy 后恢复 RAN BY 列 + "Charged items by provider" 分账块
    const lineColumns: ColumnsType<TEligibilityInvoiceLine> = [
        { title: 'CLIENT', dataIndex: 'clientName' },
        { title: 'PAYER', dataIndex: 'payerName', className: s.mut },
        {
            title: 'RUN AT',
            dataIndex: 'checkAt',
            className: s.mut,
            render: (v: string) => dayjs(v).format('MM/DD/YYYY h:mm A'),
        },
        {
            // Result 与 client 页同一状态词汇表（同一组件），两个 surface 不能对同一次 check 有不同说法
            title: 'RESULT',
            dataIndex: 'checkStatus',
            render: (v: string) => <EligibilityStatusTag status={lineTag(v)} showTip={false} />,
        },
        {
            title: 'FEE',
            key: 'fee',
            align: 'right',
            render: (_, l) => formatMoney(l.fee),
        },
    ];

    const isEmptyData = !listLoading && !keyword && !providerKeyword && statusSel.length === 0 && total === 0;

    // 空态/费用卡文案按视图二分（本 tab 只剩非 program persona；enrolled 无 eligibility 发票）
    let emptyText: string;
    let feeTip: string;
    if (isPractice) {
        emptyText =
            'No provider in this practice has eligibility charges yet. Invoices are generated per provider at the start of each month.';
        feeTip =
            'Total eligibility fees across all providers in this period: per check, plus any advanced payer searches.';
    } else {
        emptyText = `Eligibility checks accrue at ${formatMoney(
            ELIGIBILITY_PRICE_CENTS / 100,
        )} per check. Klarity generates your invoice at the start of the next month — nothing is charged when a check runs.`;
        feeTip = 'Total eligibility fees in this period: per check, plus any advanced payer searches.';
    }
    const detailLines = detail?.lines || [];

    return (
        <div className={s.wrap}>
            {/* 顶行对齐原型 cf-cred-top：子 tab 按钮在左，时间选择器同一行靠右，无 Invoices 标题 */}
            <div className={s.subNav}>
                <button type="button" className={s.activeNav}>
                    Eligibility checks
                </button>
                <div className={s.periodSelect}>
                    <Select
                        value={months}
                        onChange={(m) => setMonths(m)}
                        style={{ minWidth: 160 }}
                        dropdownStyle={{ minWidth: 160 }}
                    >
                        {[1, 2, 3, 4, 5, 6].map((m) => (
                            <Option key={m} value={m}>
                                {`Last ${m} month${m > 1 ? 's' : ''}`}
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>

            {isEmptyData ? (
                <div className={s.emptyState}>
                    <h2>No invoices yet</h2>
                    <p>{emptyText}</p>
                </div>
            ) : (
                <>
                    {/* 三张指标卡（本 tab 只剩非 program persona） */}
                    <div className={s.metrics}>
                        <MetricCard label="Eligibility fees" tip={feeTip} value={formatMoney(summary?.feeTotal || 0)} />
                        <MetricCard
                            label="Checks run"
                            tip={
                                isPractice
                                    ? 'Every eligibility check run across all providers in this period.'
                                    : 'Every eligibility check run in this period. Each check is billed when it runs.'
                            }
                            value={String(summary?.checksRun || 0)}
                        />
                        <MetricCard
                            label="Payer searches"
                            tip="Advanced payer searches run in this period. These are charged on every plan."
                            value={String(summary?.payerSearches || 0)}
                        />
                    </div>

                    <CommonTable<TEligibilityInvoiceListItem>
                        // practice 视图多 provider 同月的虚拟行共用同一 invoiceNumber，要拼 providerId 防撞 key
                        rowKey={(inv) => inv.invoiceId ?? `${inv.invoiceNumber}-${inv.providerId ?? ''}`}
                        columns={columns}
                        data={records}
                        loading={listLoading}
                        locale={{ emptyText: <span>No invoices match your filters.</span> }}
                        onChange={(pagination, filters, sorterArg) => {
                            setKeyword(String(filters.invoiceNumber?.[0] ?? '').trim());
                            setProviderKeyword(String(filters.provider?.[0] ?? '').trim());
                            setStatusSel((filters.status as string[]) || []);
                            const sv = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
                            const sortType = sv?.columnKey ? SORT_TYPE_BY_COLUMN[String(sv.columnKey)] : undefined;
                            if (sortType && sv?.order) {
                                setSorter({ sortType, orderType: sv.order === 'ascend' ? 'asc' : 'desc' });
                            } else {
                                setSorter({ sortType: 'ISSUED', orderType: 'desc' });
                            }
                            setPage(pagination.current || 1);
                            setPageSize(pagination.pageSize || 10);
                        }}
                        pagination={{ current: page, pageSize, total, showSizeChanger: true }}
                        scroll={{ x: 'max-content' }}
                        onRow={(inv) => ({ onClick: () => openDetail(inv), className: s.invoiceRow })}
                    />
                </>
            )}

            <Modal
                open={!!detail}
                onCancel={() => setDetail(null)}
                footer={null}
                width={960}
                style={{ top: 40 }}
                bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
                title={
                    <div className={s.modalHead}>
                        <div>
                            <div className={s.modalTitle}>Invoice {detail?.invoiceNumber || ''}</div>
                            {/* practice 视图标注归属 provider（详情 VO 不带，来自点击行） */}
                            <div className={s.modalSub}>
                                {detailProvider ? `Eligibility checks · ${detailProvider}` : 'Eligibility checks'}
                            </div>
                        </div>
                        {detail?.invoiceId != null && (
                            <span className={s.modalLink} onClick={() => downloadPdf(detail.invoiceId!)}>
                                <DownloadOutlined /> Download invoice
                            </span>
                        )}
                    </div>
                }
            >
                {detail && (
                    <>
                        <div className={s.modalMeta}>
                            <div>
                                <span>Billing period</span>
                                {formatPeriod(detail.billingFrom, detail.billingTo)}
                            </div>
                            <div>
                                <span>Issued</span>
                                {formatDateMDY(detail.issueDate)}
                            </div>
                            <div>
                                <span>Due</span>
                                {formatDateMDY(detail.dueDate)}
                            </div>
                            <div>
                                <span>Total fee</span>
                                <strong>{formatMoney(detail.fee)}</strong>
                            </div>
                        </div>
                        <Table<TEligibilityInvoiceLine>
                            rowKey="recordId"
                            columns={lineColumns}
                            dataSource={detailLines}
                            loading={detailLoading}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            locale={{ emptyText: 'No checks were charged in this period.' }}
                            summary={() =>
                                detailLines.length > 0 ? (
                                    <Table.Summary.Row className={s.totalRow}>
                                        <Table.Summary.Cell index={0} colSpan={3} align="right">
                                            Totals
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                            <span className={s.mut}>{detailLines.length} charged</span>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} align="right">
                                            {formatMoney(detail.fee)}
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                ) : null
                            }
                        />
                    </>
                )}
            </Modal>
        </div>
    );
};

export default EligibilityCheckInvoices;
